import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ConfirmModal from './ConfirmModal';
import { useAuth } from '../contexts/AuthContext';

const InvoiceDetailsModal = ({ show, handleClose, invoiceBatch, refreshData }) => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [confirmShow, setConfirmShow] = useState(false);
    const [rejectShow, setRejectShow] = useState(false);

    // Grouping State
    const [groupedItems, setGroupedItems] = useState([]);

    // Barcode Modal State
    const [showBarcodeModal, setShowBarcodeModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    // Editable state
    const [editingProductId, setEditingProductId] = useState(null);
    const [editForm, setEditForm] = useState({ quantity: '', price: '', selling_price: '', first_price: '' });

    useEffect(() => {
        if (show && invoiceBatch) {
            fetchItems();
        }
    }, [show, invoiceBatch]);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const res = await fetch(`/api/invoices/${invoiceBatch.batchid}/items`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'x-owner-id': user?.ownerId || user?.id || '',
                }
            });
            if (!res.ok) throw new Error('Failed to fetch invoice items');
            const data = await res.json();
            setItems(data);
            groupItems(data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const groupItems = (data) => {
        const groups = {};
        data.forEach(item => {
            const key = item.productid;
            if (!groups[key]) {
                groups[key] = {
                    productid: item.productid,
                    product_name: item.product_name,
                    quantity_purchased: 0,
                    costperunit: item.costperunit,
                    sellingprice: item.sellingprice,
                    first_price: item.first_price,
                    currency: item.currency,
                    ids: [],
                    barcodes: [],
                    isSpecific: !!item.barcode // Assume specific if barcode exists
                };
            }
            groups[key].quantity_purchased += parseFloat(item.quantity_purchased);
            groups[key].ids.push(item.id);
            if (item.barcode) {
                groups[key].barcodes.push({ id: item.id, barcode: item.barcode });
            }
        });
        setGroupedItems(Object.values(groups));
    };

    const handleConfirm = async () => {
        try {
            const res = await fetch('/api/invoices/confirm', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'x-owner-id': user?.ownerId || user?.id || '',
                },
                body: JSON.stringify({ batchId: invoiceBatch.batchid })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            toast.success('Invoys tasdiqlandi!');
            setConfirmShow(false);
            handleClose();
            refreshData();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleReject = async () => {
        try {
            const res = await fetch('/api/invoices/reject', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'x-owner-id': user?.ownerId || user?.id || '',
                },
                body: JSON.stringify({ batchId: invoiceBatch.batchid })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);

            toast.info('Invoys rad etildi');
            setRejectShow(false);
            handleClose();
            refreshData();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const startEdit = (group) => {
        setEditingProductId(group.productid);
        setEditForm({
            quantity: group.quantity_purchased,
            price: group.costperunit,
            selling_price: group.sellingprice,
            first_price: group.first_price || ''
        });
    };

    const cancelEdit = () => {
        setEditingProductId(null);
        setEditForm({ quantity: '', price: '', selling_price: '', first_price: '' });
    };

    const saveEdit = async (group) => {
        try {
            const updates = {
                costperunit: editForm.price,
                sellingprice: editForm.selling_price,
                first_price: editForm.first_price
            };

            if (!group.isSpecific) {
                updates.quantity_purchased = editForm.quantity;
                updates.quantity_remaining = editForm.quantity;
            }

            const promises = group.ids.map(id =>
                fetch(`/api/invoices/items/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'x-owner-id': user?.ownerId || user?.id || '',
                    },
                    body: JSON.stringify(updates)
                })
            );

            await Promise.all(promises);

            toast.success('Updated');
            setEditingProductId(null);
            fetchItems(); // Refresh
        } catch (err) {
            toast.error(err.message);
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm('Haqiqatan ham o\'chirmoqchimisiz?')) return;
        try {
            const res = await fetch(`/api/invoices/items/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'x-owner-id': user?.ownerId || user?.id || '',
                }
            });
            if (!res.ok) throw new Error('Delete failed');
            toast.success('O\'chirildi');
            fetchItems();
            // If deleting from barcode modal, refresh that group if needed or just re-fetch which updates groups
        } catch (err) {
            toast.error(err.message);
        }
    };

    const openBarcodeModal = (group) => {
        setSelectedGroup(group);
        setShowBarcodeModal(true);
    };

    const isEditable = invoiceBatch?.invoice_status === 0;

    return (
        <>
            <Modal show={show} onHide={handleClose} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Invoys Tafsilotlari ({invoiceBatch?.batchid}) - {invoiceBatch?.invoice_status === 0 ? 'Yangi' : invoiceBatch?.invoice_status === 1 ? 'Tasdiqlangan' : 'Rad etilgan'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? <div className="text-center">Yuklanmoqda...</div> : (
                        <div className="table-responsive">
                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        <th>Mahsulot</th>
                                        <th>Miqdor</th>
                                        <th>Tannarx</th>
                                        <th>Narx (Olish)</th>
                                        <th>Narx (Sotish)</th>
                                        <th>Jami</th>
                                        <th>Shtrixkod</th>
                                        {isEditable && <th>Amallar</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedItems.map(group => (
                                        <tr key={group.productid}>
                                            <td>{group.product_name}</td>
                                            <td>
                                                {editingProductId === group.productid && !group.isSpecific ? (
                                                    <Form.Control
                                                        type="number"
                                                        value={editForm.quantity}
                                                        onChange={e => setEditForm({ ...editForm, quantity: e.target.value })}
                                                    />
                                                ) : (
                                                    group.quantity_purchased
                                                )}
                                            </td>
                                            <td>
                                                {editingProductId === group.productid ? (
                                                    <Form.Control
                                                        type="number"
                                                        value={editForm.first_price}
                                                        onChange={e => setEditForm({ ...editForm, first_price: e.target.value })}
                                                    />
                                                ) : (
                                                    Number(group.first_price || 0).toLocaleString()
                                                )}
                                            </td>
                                            <td>
                                                {editingProductId === group.productid ? (
                                                    <Form.Control
                                                        type="number"
                                                        value={editForm.price}
                                                        onChange={e => setEditForm({ ...editForm, price: e.target.value })}
                                                    />
                                                ) : (
                                                    Number(group.costperunit).toLocaleString() + ' ' + (group.currency || '')
                                                )}
                                            </td>
                                            <td>
                                                {editingProductId === group.productid ? (
                                                    <Form.Control
                                                        type="number"
                                                        value={editForm.selling_price}
                                                        onChange={e => setEditForm({ ...editForm, selling_price: e.target.value })}
                                                    />
                                                ) : (
                                                    Number(group.sellingprice).toLocaleString()
                                                )}
                                            </td>
                                            <td>
                                                {(Number(group.quantity_purchased) * Number(group.costperunit)).toLocaleString()} {group.currency}
                                            </td>
                                            <td>
                                                {group.barcodes.length > 0 ? (
                                                    <Button variant="info" size="sm" onClick={() => openBarcodeModal(group)}>
                                                        Shtrixkodlar ({group.barcodes.length})
                                                    </Button>
                                                ) : '-'}
                                            </td>
                                            {isEditable && (
                                                <td>
                                                    {editingProductId === group.productid ? (
                                                        <>
                                                            <Button size="sm" variant="success" className="me-1" onClick={() => saveEdit(group)}>Save</Button>
                                                            <Button size="sm" variant="secondary" onClick={cancelEdit}>Cancel</Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button size="sm" variant="primary" className="me-1" onClick={() => startEdit(group)}>Edit</Button>
                                                            <Button size="sm" variant="danger" onClick={() => {
                                                                if (window.confirm('Butun guruhni o\'chirasizmi?')) Promise.all(group.ids.map(id => deleteItem(id)));
                                                            }}>Del</Button>
                                                        </>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>Yopish</Button>
                    {isEditable && items.length > 0 && (
                        <>
                            <Button variant="danger" onClick={() => setRejectShow(true)}>Rad etish</Button>
                            <Button variant="success" onClick={() => setConfirmShow(true)}>Tasdiqlash</Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Barcode List Modal */}
            <Modal show={showBarcodeModal} onHide={() => { setShowBarcodeModal(false); fetchItems(); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Shtrixkodlar - {selectedGroup?.product_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table striped bordered size="sm">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Shtrixkod</th>
                                {isEditable && <th>Amal</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {selectedGroup?.barcodes.map((b, idx) => (
                                <tr key={b.id}>
                                    <td>{idx + 1}</td>
                                    <td>{b.barcode}</td>
                                    {isEditable && (
                                        <td>
                                            <Button variant="danger" size="sm" onClick={() => { deleteItem(b.id); setTimeout(fetchItems, 100); }}>O'chirish</Button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
            </Modal>

            <ConfirmModal
                show={confirmShow}
                onCancel={() => setConfirmShow(false)}
                onConfirm={handleConfirm}
                message="Haqiqatan ham bu invoysni tasdiqlaysizmi? Bu mahsulotlarni omborga qo'shadi."
            />

            <ConfirmModal
                show={rejectShow}
                onCancel={() => setRejectShow(false)}
                onConfirm={handleReject}
                message="Haqiqatan ham bu invoysni rad etasizmi?"
            />
        </>
    );
};

export default InvoiceDetailsModal;
